import { useEffect, useState } from "react";
import { ApiError, fetchCertificateEvidence } from "../api";
import type { CertificateEvidence } from "../types";

/**
 * The AI's driving licence, made visible.
 *
 * This is the offline half of the P3 closed loop: before the automation got its authority,
 * the attacker's strength was swept until a pre-registered safety gate broke, and the
 * distance to that break is the licence's headroom. The panel's job is to make the
 * project's central idea legible to a non-engineer: the two ways to attack this system are
 * split by whether the RUNNING system can see them, and each half is defended by a
 * different mechanism (visible axis -> runtime governor; invisible axis -> this
 * certificate). Every number is fetched from the allow-listed projection of
 * `autonomy_certificate_v3.json` - nothing is computed or hardcoded here.
 */

function pct(value: number): string {
  return `${(value * 100).toFixed(0)}%`;
}

/** A 0..1 axis drawn as a bar with two markers: where we assumed the attacker is, and
 *  where the safety gate actually breaks. The gap between them IS the licence. */
function HeadroomGauge({
  assumed,
  breakpoint,
}: {
  assumed: number;
  breakpoint: number;
}) {
  return (
    <div className="headroom-gauge" data-testid="headroom-gauge">
      <div className="headroom-track">
        <div className="headroom-safe" style={{ width: `${breakpoint * 100}%` }} />
        <div className="headroom-marker assumed" style={{ left: `${assumed * 100}%` }}>
          <span>가정한 사기꾼 {pct(assumed)}</span>
        </div>
        <div className="headroom-marker break" style={{ left: `${breakpoint * 100}%` }}>
          <span>한계선 {pct(breakpoint)}</span>
        </div>
      </div>
      <div className="headroom-scale">
        <span>0% (본인확인을 전혀 못 뚫음)</span>
        <span>100% (전부 뚫음)</span>
      </div>
    </div>
  );
}

export function CertificatePanel() {
  const [evidence, setEvidence] = useState<CertificateEvidence | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCertificateEvidence()
      .then(setEvidence)
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError && err.status === 404
            ? "Not measured yet — 인증서 산출물(ml/artifacts/autonomy_certificate_v3.json)이 없습니다."
            : "Not measured yet — 인증서를 불러오지 못했습니다.",
        );
      });
  }, []);

  if (error) {
    return (
      <section className="card">
        <h3>📜 AI의 운전면허증</h3>
        <p className="muted">{error}</p>
      </section>
    );
  }
  if (!evidence) {
    return (
      <section className="card">
        <h3>📜 AI의 운전면허증</h3>
        <p className="muted">불러오는 중…</p>
      </section>
    );
  }

  const bp = evidence.breakpoint;
  const cheapest = evidence.cheapest_attack;
  const invisible = evidence.coverage.find((c) => !c.runtime_observable);
  const visible = evidence.coverage.find((c) => c.runtime_observable);

  return (
    <section className="card">
      <h3>📜 AI의 운전면허증 — 어디까지 믿어도 되는지, 미리 재봤어요</h3>
      <p>
        방금 공격해 본 AI는 <strong>무면허로 일하지 않아요.</strong> 자동 판단 권한을 받기
        전에, <strong>사기꾼이 얼마나 세져야 이 시스템이 위험해지는지</strong>를 먼저
        측정해서 면허증에 적어뒀습니다. 아래가 그 실제 면허증 내용이에요.
      </p>

      <div className={`cert-status ${evidence.status === "ACTIVE" ? "ok" : "bad"}`}>
        <span className="cert-badge">
          {evidence.status === "ACTIVE" ? "✅ 면허 유효" : "🚫 면허 정지"}
        </span>
        <span className="muted">
          {evidence.certificate_id} · 시스템이 켜질 때마다 모델·정책의 지문(체크섬)을 대조해
          위조를 확인합니다
        </span>
      </div>

      <h4>🧪 시험 방법: 사기꾼을 조금씩 세게 만들어보기</h4>
      <p>
        이 시스템의 중간 위험 구간은 <strong>본인 확인(휴대폰 확인)</strong>으로 지켜져요.
        그래서 &quot;사기꾼이 본인 확인을 뚫는 확률&quot;을 0%에서 조금씩 올려가며, 미리
        봉투에 넣어둔 안전 기준이 <strong>처음 깨지는 지점</strong>을 찾았습니다:
      </p>
      <HeadroomGauge assumed={bp.assumed_attacker} breakpoint={bp.breakpoint} />
      <p>
        가정한 사기꾼({pct(bp.assumed_attacker)})보다 사기꾼이{" "}
        <strong>{pct(bp.headroom)}p 더 세져도</strong> 안전 기준은 버팁니다. 이 여유분이
        면허의 핵심이에요 — 그리고 한계선({pct(bp.breakpoint)})을 넘으려면 비밀번호 탈취로는
        부족하고 <strong>등록된 휴대폰 자체를 손에 넣어야</strong> 합니다. 공격자의 급이
        달라져야 한다는 뜻이에요.
      </p>

      <h4>🕶️ 이 프로젝트에서 제일 아끼는 아이디어: 공격은 두 종류다</h4>
      <div className="coverage-grid">
        {visible && (
          <div className="coverage-card">
            <strong>👁️ 티가 나는 공격 — 위장</strong>
            <p>
              사기를 &quot;안전해 보이는 구간&quot;으로 숨기면, 그 구간의{" "}
              <strong>점유율이 올라가는 게 실시간으로 보여요.</strong> 그래서 이 축은{" "}
              <strong>경비원(Runtime Governor)</strong>이 지킵니다 — 위에서 공격 실험했을 때
              자동화를 멈춘 바로 그 장치예요.
            </p>
          </div>
        )}
        {invisible && (
          <div className="coverage-card">
            <strong>🫥 티가 안 나는 공격 — 본인확인 뚫기</strong>
            <p>
              사기꾼이 본인 확인을 통과하면, 정상 고객의 통과와{" "}
              <strong>구별할 방법이 원리적으로 없어요</strong> (정답지가 없으니까요). 실시간
              감시로는 못 잡는 축이라, <strong>위에서 잰 여유분(면허증)</strong>이 유일한
              방어입니다.
            </p>
          </div>
        )}
      </div>
      <p>
        재미있는 결과: 가장 <strong>싼</strong> 공격은 위장(겨우 {pct(cheapest.disguise_share)}만
        위장해도 무방비라면 탐지율이 {(cheapest.recall_under_attack * 100).toFixed(1)}%로
        무너짐)인데, <strong>하필 그 축이 경비원 눈에 보이는 축</strong>이에요. 비싼 공격은
        안 보이지만 면허의 여유분이 막고, 싼 공격은 보이니까 경비원이 막고 — 두 방어가
        중복이 아니라 <strong>서로 못 보는 곳을 나눠 맡은 겁니다.</strong>
      </p>

      <details className="fine-print">
        <summary>🔬 기술 상세 (원문 수치)</summary>
        <ul>
          <li>
            축: <code>{bp.axis}</code>, 안전 범위 [{evidence.safe_range[0]},{" "}
            {evidence.safe_range[1]}], breakpoint {bp.breakpoint} (그 지점 recall{" "}
            {bp.recall_at_breakpoint.toFixed(4)}), 가정 공격자 {bp.assumed_attacker} →
            headroom +{bp.headroom}
          </li>
          <li>
            최저비용 공격 경로: {cheapest.leans_on} (disguise {cheapest.disguise_share},
            pass-rate {cheapest.fraud_step_up_pass_rate}, cost {cheapest.attacker_cost}) —
            2차원 프론티어 {evidence.frontier.length}개 점 측정
          </li>
          <li>
            챌린지 요소: <code>{evidence.challenge_factor}</code> — breakpoint(
            {bp.breakpoint})가 credential-only 공격자 범위(≈{bp.assumed_attacker}) 밖에
            있음을 확인하는 위협모델 브리지
          </li>
          <li>
            원본: <code>ml/artifacts/autonomy_certificate_v3.json</code> (
            <code>GET /v1/evidence/certificate</code>는 allow-list 투영)
          </li>
        </ul>
      </details>
    </section>
  );
}
